import { Fragment, type ReactNode } from 'react';
import { SortHead } from './SortHead';
import { Skeleton } from './Skeleton';
import { fmtDate } from '../../lib/dates';

// ── The closed cell-type set ────────────────────────────────────────────────
// Every value in a table row renders through ONE of these primitives, so a cell
// can't invent its own size/weight — it picks a type. Typography lives entirely
// in the --size-* / --weight-* / --ink-* tokens (see the ".cell-*" rules in
// app.css); these components only pick which type.
export function Lead({ children }: { children: ReactNode }) {
  return <span className="cell-lead">{children}</span>;
}
export function Body({ children }: { children: ReactNode }) {
  return <span className="cell-body">{children}</span>;
}
export function Sub({ children }: { children: ReactNode }) {
  return <span className="cell-sub">{children}</span>;
}
export function Num({ children }: { children: ReactNode }) {
  return <span className="cell-num">{children}</span>;
}
// A stacked pair: a lead line over a quieter sub line (dates/nights, name/breed).
export function Stack({ lead, sub }: { lead: ReactNode; sub?: ReactNode }) {
  return (
    <div className="cell-stack">
      <span className="cell-lead">{lead}</span>
      {sub != null && sub !== '' && <span className="cell-sub">{sub}</span>}
    </div>
  );
}
// A timestamp cell (the "Date added" / "Last updated" columns): one formatting
// rule + one style, shared by every table so the two date columns can never
// drift apart. Accepts anything fmtDate does; null/undefined render as fmtDate's
// own placeholder.
export function DateCell({ value }: { value: string | number | Date | null | undefined }) {
  return (
    <div className="bt-added mono" role="cell">
      {fmtDate(value)}
    </div>
  );
}

// ── The table ───────────────────────────────────────────────────────────────
// One primitive for every table in the app. A column declares its header (label
// + optional sort key) and a `cell` renderer that returns the complete grid
// child for that column. The component owns the header/row scaffolding — no
// per-table duplication — while the page owns column set, sort, and click.
//
// It emits the shared `.bookings-table` / `.bt-row` shell so all existing grid,
// status-stripe, hover and responsive CSS applies unchanged; per-table column
// widths come from the container `className` (e.g. 'bookings-table owners-table').
export interface Column<T, K extends string> {
  key: K;
  label: string;
  sortable?: boolean;
  // Extra header class (e.g. right-aligned numeric column).
  headClass?: string;
  // Returns the complete grid child for this column (its own wrapper + typed
  // content). Use the typed primitives above (or a component like DogCell).
  cell: (row: T) => ReactNode;
}

export interface DataTableProps<T, K extends string> {
  columns: Column<T, K>[];
  rows: T[];
  rowKey: (row: T) => string;
  // Sort is controlled by the parent (applied server-side). Optional so the
  // table renders standalone in tests; when absent, headers are plain labels.
  sort?: { key: K; dir: 'asc' | 'desc' };
  onSort?: (k: K) => void;
  onRowClick?: (row: T) => void;
  // Per-row modifier classes (status stripe, cancelled, today-flag, boarding…).
  rowClassName?: (row: T) => string;
  // Container class — carries the grid variant. Defaults to the plain table.
  className?: string;
  // While true, keep the header row and render a column-matched skeleton body
  // instead of the rows — so a sort/search re-fetch never reloads the header,
  // and the placeholder aligns to the real columns (not a generic block).
  loading?: boolean;
  // Skeleton rows to show while loading (enough to fill the visible area).
  skeletonRows?: number;
  // Accessible announcement while loading, e.g. "Loading bookings…".
  loadingLabel?: string;
  empty?: ReactNode;
}

export function DataTable<T, K extends string>({
  columns,
  rows,
  rowKey,
  sort,
  onSort = () => {},
  onRowClick,
  rowClassName,
  className = 'bookings-table',
  loading = false,
  skeletonRows = 8,
  loadingLabel = 'Loading…',
  empty,
}: DataTableProps<T, K>) {
  return (
    <div className={className} role="table" aria-busy={loading || undefined}>
      <div className="bt-row head" role="row">
        {columns.map((c) =>
          c.sortable && sort ? (
            <SortHead
              key={c.key}
              k={c.key}
              label={c.label}
              sortKey={sort.key}
              dir={sort.dir}
              onSort={onSort}
              className={c.headClass}
            />
          ) : (
            <div key={c.key} className={'bt-head-btn' + (c.headClass ? ' ' + c.headClass : '')} role="columnheader">
              <span>{c.label}</span>
            </div>
          ),
        )}
      </div>

      {loading ? (
        <>
          <span className="sr-only" role="status">{loadingLabel}</span>
          {Array.from({ length: skeletonRows }, (_, i) => (
            <div key={i} className="bt-row bt-skeleton" role="row" aria-hidden="true">
              {columns.map((c) => (
                <Skeleton key={c.key} variant="line" />
              ))}
            </div>
          ))}
        </>
      ) : (
        <>
          {rows.map((row) => {
            const mods = rowClassName ? rowClassName(row) : '';
            return (
              <div
                key={rowKey(row)}
                className={'bt-row' + (onRowClick ? ' clickable' : '') + (mods ? ' ' + mods : '')}
                role="row"
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                // A clickable row is a control: focusable, and opened by Enter or
                // Space as the desk's rows are — the gap StayTable refused to
                // inherit, closed for every list (viewings-in-the-bookings-list).
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault(); // Space scrolls the region this sits in
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((c) => (
                  <Fragment key={c.key}>{c.cell(row)}</Fragment>
                ))}
              </div>
            );
          })}

          {empty}
        </>
      )}
    </div>
  );
}
